// Library imports
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import store, { RootState } from "store";
import styled from "styled-components";

// Local imports
import FloatingPopover from "modules/Common/Components/FloatingPopover";
import { getDesignations, getOrganizations, importList, importListProgress } from "../actions";
import {
  reloadAudienceContacts,
  reloadListDetails,
  resetAudienceListDetailsProfiled,
  resetAudienceLists,
  resetAudiencePeopleTabData,
  updateAudienceLists,
} from "../Lists/actions";
import FloatingPopoverComplete from "./FloatingPopoverComplete";
import FloatingPopoverImport from "./FloatingPopoverImport";
import FloatingPopoverImportMap from "./FloatingPopoverImportMap";
import FloatingPopoverProgress from "./FloatingPopoverProgress";
import { GLOBAL_ACTION, setMainNavBackOverlay, setStickyHeaderBackOverlay } from "modules/GlobalActions";
import { getRecommendedPeople, resetRecommendedContacts } from "modules/Campaigns/CampaignAssets/actions";
import { getQueryListId } from "utils";

interface Props {
  showImportListAsider: boolean;
  setShowImportListAsider: any;
  handleAddPeople?: any;
  inPublishEmail?: boolean;
  tabState?: string;
}

const ImportListFlow: React.FC<Props> = ({ showImportListAsider, setShowImportListAsider, handleAddPeople }) => {
  // Local States
  const location = useLocation();
  const isListView = location.pathname === "/audiences/lists/view"; // check for importing inside list
  const [showImportListMapAsider, setShowImportListMapAsider] = useState(false);
  const [showImportProgressAsider, setShowImportProgressAsider] = useState(false);
  const [showImportCompleteAsider, setShowImportCompleteAsider] = useState(false);
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(new File([], ""));
  const [inputListName, setInputListName] = useState("");
  const [error, setError] = useState("");
  const [fieldsMapping, setFieldsMapping] = useState({
    first_name: "0",
    last_name: "0",
    email: "0",
    designation: "0",
    company_name: "0",
    company_website: "0",
  });
  const [nonMappedFields, setNonMappedFields] = useState(Array<string>());
  const [disableImportNowBtn, setDisableImportNowBtn] = useState("");
  const [progressImportedContacts, setProgressImportedContacts] = useState(0);
  const [progressTotalContacts, setProgressTotalContacts] = useState(0);
  const [createdListId, setCreatedListId] = useState(0);
  const [success, setSuccess] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  // Redux States
  const importListSuccess = useSelector((state: RootState) => state.audienceLists.list_import);
  const backOverlayOnStickyHeader = useSelector((state: RootState) => state.uiReducer.backOverlayOnStickyHeader);
  const backOverlayOnMainNav = useSelector((state: RootState) => state.uiReducer.backOverlayOnMainNav);
  const listId: string = useSelector((state: RootState) => state.audienceListDetails.listId);
  const listName = useSelector((state: RootState) => state.audienceListDetails.listName);
  const audienceFiltersApplied: boolean = useSelector(
    (state: RootState) => state.uiReducer.audienceFiltersApplied
  );
  const searchTermListDetails = useSelector((state: RootState) => state.audienceListDetails.searchTermListDetails);

  // Component Logic
  useEffect(() => {
    if (importListSuccess) setFileName("");
  }, [importListSuccess]);

  function handleChangeFile() {
    setShowImportListMapAsider(false);
    setShowImportListAsider(true);
  }

  function resetInputs() {
    setFileName("");
    setSelectedFile(new File([], ""));
    setInputListName("");
    setError("");
    setFieldsMapping({
      first_name: "0",
      last_name: "0",
      email: "0",
      designation: "0",
      company_name: "0",
      company_website: "0",
    });
    setNonMappedFields(Array<string>());
    setDisableImportNowBtn("");
  }

  function handleCancelImport() {
    if (showImportListAsider == true) setShowImportListAsider(false);
    if (showImportListMapAsider == true) setShowImportListMapAsider(false);
    closeHeaderSiderOverlays();
    resetInputs();
    store.dispatch({
      type: GLOBAL_ACTION.IMPORT_LIST_POPOVER,
      payload: { importListAsiderState: false },
    });
  }

  function closeHeaderSiderOverlays() {
    if (backOverlayOnStickyHeader) setStickyHeaderBackOverlay(false);
    if (backOverlayOnMainNav) setMainNavBackOverlay(false);
  }

  function handleImportList(updatedMapping: {}) {
    const newListName = inputListName.trim()
      ? inputListName.trim().replaceAll(",", " ")
      : fileName.replace(/\.[^/.]+$/, "");
    importList(newListName, JSON.stringify(updatedMapping), selectedFile)
      .then((res) => {
        if (res.data.success === true) {
          setShowImportListMapAsider(false);
          setShowImportProgressAsider(true);
          setDisableImportNowBtn("");
          updateImportProgress(res.data.list_id);
          setCreatedListId(res.data.list_id);
          setErrorMessage("");
          if (getQueryListId() > 0) {
            getOrganizations(getQueryListId().toString());
            getDesignations(getQueryListId().toString());
          } else {
            getOrganizations();
            getDesignations();
          }
        } else if (res.data.success === false) {
          setDisableImportNowBtn("");
          setErrorMessage(res.data.message);
        }
      })
      .catch((err) => {
        resetInputs();
        console.error(err);
      });
  }

  function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function updateImportProgress(listId: number) {
    importListProgress(listId).then(async (res) => {
      const listData = res.data.list_details[0];
      if (res.data.success === true) {
        let importedContacts = res.data.imported_contacts;
        let totalContacts = res.data.total_contacts;
        setProgressTotalContacts(totalContacts);
        if (importedContacts !== -1) {
          setProgressImportedContacts(importedContacts);
        }
        if (res.data.import_status === "Error") {
          setSuccess(false);
        } else if (importedContacts < totalContacts && res.data.import_status !== "Completed") {
          setTimeout(() => {
            updateImportProgress(listId);
          }, 2000);
        } else if (res.data.import_status === "Completed") {
          // importListCount(listData.list_id, totalContacts);
          // the following call will update the recipients list in publish
          // if (inPublishEmail) linkAudienceWithAsset(getQueryCampaignId(), [listData.list_id], []);
          await sleep(200);
          setShowImportProgressAsider(false);

          resetRecommendedContacts();
          getRecommendedPeople();

          if (!location.pathname.includes("/publish")) {
            setShowImportCompleteAsider(true);
          } else {
            closeHeaderSiderOverlays();
          }
          updateAudienceLists(res.data.list_details);

          // import list within a list and reload contacts in that list
          let listId = getQueryListId();
          if (listId) {
            resetAudienceListDetailsProfiled();
            if (!audienceFiltersApplied) {
              reloadListDetails(true, "profiled"); // set true to call contacts api in ProfiledList.tsx
            } else {
              store.dispatch({
                type: GLOBAL_ACTION.APPLY_AUDIENCE_FILTERS,
              });
            }
            resetAudienceLists();
          }

          // if list import on People tab reset contacts and reload to list them in People Tab
          if (location.pathname.includes("/people")) {
            resetAudiencePeopleTabData();
            if (!audienceFiltersApplied) {
              reloadAudienceContacts(true);
            } else {
              store.dispatch({
                type: GLOBAL_ACTION.APPLY_AUDIENCE_FILTERS,
              });
            }
          }

          resetInputs();
          setProgressTotalContacts(0);
          if (handleAddPeople) {
            handleAddPeople(res.data.list_details[0].list_id, res.data.list_details[0].list_name, "list");
          }
        }
      }
    });
  }

  useEffect(() => {
    if (showImportListAsider || showImportListMapAsider || showImportProgressAsider || showImportCompleteAsider) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      setErrorMessage("");
    };
  }, [showImportListAsider, showImportListMapAsider, showImportProgressAsider, showImportCompleteAsider]);

  return (
    <Wrapper>
      <div
        className={`${
          showImportListAsider || showImportListMapAsider || showImportProgressAsider || showImportCompleteAsider
            ? "layover"
            : ""
        }`}
      ></div>
      <FloatingPopover showRoyIcon={false} contentType="email" isPopoverVisible={showImportListAsider}>
        <FloatingPopoverImport
          setShowImportListAsider={setShowImportListAsider}
          setShowImportListMapAsider={setShowImportListMapAsider}
          inputListName={inputListName}
          setInputListName={setInputListName}
          error={error}
          setError={setError}
          fileName={fileName}
          setFileName={setFileName}
          fieldsMapping={fieldsMapping}
          setFieldsMapping={setFieldsMapping}
          nonMappedFields={nonMappedFields}
          setNonMappedFields={setNonMappedFields}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          handleCancelImport={handleCancelImport}
        />
      </FloatingPopover>

      <FloatingPopover
        showRoyIcon={false}
        contentType="email"
        overlayClass="floating-popover-reminder big-popover"
        isPopoverVisible={showImportListMapAsider}
      >
        <FloatingPopoverImportMap
          fileName={fileName}
          setShowImportListMapAsider={setShowImportListMapAsider}
          handleChangeFile={handleChangeFile}
          errorMessage={errorMessage}
          inputListName={
            listId && listName && isListView
              ? listName
              : inputListName.trim() !== ""
              ? inputListName.trim().replaceAll(",", " ")
              : fileName.replace(/\.[^/.]+$/, "")
          }
          fieldsMapping={fieldsMapping}
          setFieldsMapping={setFieldsMapping}
          nonMappedFields={nonMappedFields}
          setNonMappedFields={setNonMappedFields}
          handleImportList={handleImportList}
          disableImportNowBtn={disableImportNowBtn}
          setDisableImportNowBtn={setDisableImportNowBtn}
          handleCancelImport={handleCancelImport}
        />
      </FloatingPopover>

      <FloatingPopover
        showRoyIcon={false}
        contentType="email"
        overlayClass="floating-popover-reminder bg-none"
        isPopoverVisible={showImportProgressAsider}
      >
        <FloatingPopoverProgress
          fileName={fileName}
          importedContacts={progressImportedContacts}
          totalContacts={progressTotalContacts}
          setShowImportProgressAsider={setShowImportProgressAsider}
          closeHeaderSiderOverlays={closeHeaderSiderOverlays}
          resetInputs={resetInputs}
          success={success}
        />
      </FloatingPopover>

      <FloatingPopover showRoyIcon={false} contentType="email" isPopoverVisible={showImportCompleteAsider}>
        <FloatingPopoverComplete
          listId={createdListId}
          importedContacts={progressImportedContacts}
          setShowImportCompleteAsider={setShowImportCompleteAsider}
          closeHeaderSiderOverlays={closeHeaderSiderOverlays}
        />
      </FloatingPopover>
    </Wrapper>
  );
};

export default ImportListFlow;

const Wrapper = styled.div`
  .layover {
    height: 100vh;
    width: 100vw;
    background-color: black;
    top: 0;
    left: 0;
    position: fixed;
    opacity: 0.6;
    z-index: 15;
  }
`;
